<script setup>
import { ref, computed } from 'vue';
import CloseIcon from '@/js/core/components/icons/CloseIcon.vue';
import { useDisclosure } from '@/js/core/composables/useDisclosure';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const filterEl = ref(null);
const panelId = 'tray-type-filter-dropdown';
const {
  isExpanded: isOpen,
  toggle: toggleDropdown,
  close,
  triggerAttrs,
} = useDisclosure(filterEl, { panelId });

const selectedLabels = computed(() => {
  return props.options
    .filter(option => props.modelValue.includes(option.value))
    .map(option => option.label);
});

const selectOption = value => {
  const next = props.modelValue.includes(value)
    ? props.modelValue.filter(v => v !== value)
    : [...props.modelValue, value];

  emit('update:modelValue', next);
};

const clearFilter = () => {
  emit('update:modelValue', []);
  close();
};
</script>

<template>
  <div ref="filterEl" class="tray-type-filter">
    <button
      type="button"
      class="tray-type-filter__toggle"
      :class="{ 'tray-type-filter__toggle--active': isOpen }"
      v-bind="triggerAttrs"
      @click="toggleDropdown"
    >
      <span class="tray-type-filter__indicator"></span>
      <span class="tray-type-filter__label">Tray Types</span>
      <span class="tray-type-filter__triangle">
        <svg viewBox="0 0 501.62 501.62" xmlns="http://www.w3.org/2000/svg">
          <polygon
            points="250.81 500.5 500.81 0.5 0.81 0.5 250.81 500.5"
            vector-effect="non-scaling-stroke"
          ></polygon>
        </svg>
      </span>
    </button>
    <div class="tray-type-filter__selected-list-container">
      <div v-if="modelValue.length" class="tray-type-filter__selected-list">
        <div class="tray-type-filter__listing-label">Listing:</div>
        <div v-for="label in selectedLabels" :key="label" class="tray-type-filter__selected-label">
          {{ label }}
          <button
            type="button"
            class="tray-type-filter__remove"
            :aria-label="'Remove ' + label + ' filter'"
            @click="selectOption(options.find(o => o.label === label).value)"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <button
        v-if="modelValue.length"
        type="button"
        class="tray-type-filter__option tray-type-filter__option--clear"
        @click="clearFilter"
      >
        <CloseIcon />
        Clear Filters
      </button>
    </div>
    <div v-if="isOpen" :id="panelId" class="tray-type-filter__dropdown-container">
      <div class="tray-type-filter__dropdown">
        <button
          v-for="(option, index) in options"
          :key="option.value"
          type="button"
          class="tray-type-filter__option"
          :class="{
            'tray-type-filter__option--selected':
              modelValue.includes(option.value) || (index === 0 && modelValue.length === 0),
            'tray-type-filter__option--all': index === 0 && isOpen,
          }"
          @click="selectOption(option.value)"
        >
          <span class="tray-type-filter__checkbox">
            <input
              type="checkbox"
              :checked="
                modelValue.includes(option.value) || (index === 0 && modelValue.length === 0)
              "
              tabindex="-1"
              inert
            />
            <span class="tray-type-filter__checkmark"></span>
          </span>
          <span class="tray-type-filter__label">{{ option.label }}</span>
        </button>
      </div>
      <button
        type="button"
        class="tray-type-filter__close"
        aria-label="Close tray type filter"
        @click="close()"
      >
        <CloseIcon />
      </button>
    </div>
  </div>
</template>
